// ABOUTME: Email verification page component for Oráculo dos 12 Raios
// ABOUTME: Handles email verification token validation and user feedback
// TD3-123: Email verification enforcement system

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../services/pocketbaseClient';
import AdminNotificationService from '../../services/adminNotificationService';
import './EmailVerification.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Aguardando verificação...');
  const hasVerifiedRef = useRef(false); // TD3-191: Prevent duplicate verification in React StrictMode

  useEffect(() => {
    const verifyEmail = async () => {
      // TD3-191: Prevent duplicate verification in React StrictMode (development double-render)
      if (hasVerifiedRef.current) {
        console.log('🔄 Skipping duplicate verification (already processed)');
        return;
      }
      hasVerifiedRef.current = true;

      const token = searchParams.get('token');

      console.log('🔮 Email Verification - Token:', token ? 'Present' : 'Missing');

      if (!token) {
        console.log('❌ No token found in URL');
        setStatus('error');
        setMessage('Token de verificação inválido ou ausente.');
        return;
      }

      try {
        console.log('⏳ Verifying email with PocketBase...');
        // Verify email with PocketBase - returns true/void on success
        await pb.collection('OracleUsers').confirmVerification(token);

        console.log('✅ Email verified successfully!');

        // TD3-191 DUAL NOTIFICATION: Send Telegram when email verified
        // Decode JWT to get userId and email (SAFE because confirmVerification() already validated it server-side)
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const userId = tokenPayload.id;
          const userEmail = tokenPayload.email;

          console.log('📧 Sending Telegram notification for email verification:', userId);
          // Send notification with proper email and userId (extracted from validated JWT)
          await AdminNotificationService.notifyNewRegistration(userEmail, userId);
          console.log('✅ Telegram sent: User verified email');
        } catch (notificationError) {
          // Non-blocking: don't fail verification if Telegram fails
          console.warn('⚠️ Telegram notification failed (non-critical):', notificationError.message);
        }

        // TD3-191 DUAL NOTIFICATION: Second notification fires on first login (Gate 2 in AuthContext)
        // This gives admin full visibility: verification + login attempt

        // TD3-123: Refresh user data to update verified status in AuthContext (if logged in)
        if (refreshUser) {
          console.log('🔄 Refreshing user data in AuthContext...');
          await refreshUser();
          console.log('✅ User data refreshed with verified status');
        }

        // TD3-129: Show success message - user needs to login to trigger Gate 2
        // All verified users need admin approval, so always show this message
        setStatus('success');
        setMessage('Email verificado! Faça login para solicitar acesso à sua conta.');

        // Redirect to auth page after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      } catch (error) {
        console.error('❌ Email verification error:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          data: error.data
        });

        let errorMessage = 'Erro ao verificar email. Tente novamente.';

        if (error.message && error.message.includes('token')) {
          errorMessage = 'Token de verificação expirado ou inválido.';
        } else if (error.message && error.message.includes('already')) {
          errorMessage = 'Este email já foi verificado anteriormente.';
        }

        console.log('📝 Setting error message:', errorMessage);
        setStatus('error');
        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams, navigate, refreshUser]);

  const handleBackToLogin = () => {
    navigate('/auth');
  };

  return (
    <div className="email-verification-container">
      {/* Mobile warning */}
      <div className="oracle-mobile-warning">
        <h2>Oráculo dos 12 Raios</h2>
        <p>
          Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
          Para uma melhor experiência, acesse em um dispositivo maior.
        </p>
      </div>

      {/* Main verification content */}
      <div className="verification-main">
        <div className="verification-card">
          <div className="verification-icon">
            {status === 'verifying' && <span className="icon-spinner">🔮</span>}
            {status === 'success' && <span className="icon-success">✨</span>}
            {status === 'error' && <span className="icon-error">⚠️</span>}
          </div>

          <h1>Verificação de Email</h1>

          <div className={`verification-message ${status}`}>
            {message}
          </div>

          {status === 'success' && (
            <div className="verification-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <p>Redirecionando para o painel...</p>
            </div>
          )}

          {status === 'error' && (
            <button
              onClick={handleBackToLogin}
              className="back-to-login-btn primary-cta"
            >
              Voltar para Login
            </button>
          )}
        </div>

        <footer className="verification-footer">
          <p>Oráculo dos 12 Raios • v1.0 • by Buenaar</p>
        </footer>
      </div>
    </div>
  );
};

export default EmailVerification;
